// Bezeichnungen und Hilfen für Orte und Routen; ohne Node-Abhängigkeiten (auch im Browser nutzbar).

export const PLACE_TYPES = {
  beach: { label: "Strand", plural: "Strände" },
  village: { label: "Ort", plural: "Orte & Dörfer" },
  viewpoint: { label: "Aussichtspunkt", plural: "Aussichtspunkte" },
  nature: { label: "Natur", plural: "Natur & Schluchten" },
  sight: { label: "Sehenswürdigkeit", plural: "Sehenswürdigkeiten" },
  restaurant: { label: "Restaurant", plural: "Restaurants & Cafés" },
  hotel: { label: "Unterkunft", plural: "Unterkünfte" },
  bar: { label: "Bar", plural: "Bars" },
  club: { label: "Club", plural: "Clubs" },
  wellness: { label: "Wellness", plural: "Wellness & Sauna" },
  shop: { label: "Shop", plural: "Shops" },
  venue: { label: "Event-Location", plural: "Event-Locations" },
  event: { label: "Veranstaltung", plural: "Veranstaltungen" },
  other: { label: "Sonstiges", plural: "Sonstiges" },
} as const;
export type PlaceType = keyof typeof PLACE_TYPES;
export const PLACE_TYPE_KEYS = Object.keys(PLACE_TYPES) as [PlaceType, ...PlaceType[]];

/**
 * Betriebsstatus (unabhängig vom Veröffentlichungsstatus); nur „closed“ blendet einen Ort öffentlich aus.
 * „temporary“ bleibt in Listen sichtbar, fällt aber aus dem Routenplaner.
 */
export const BUSINESS_STATUS = { active: "Geöffnet", temporary: "Vorübergehend geschlossen", closed: "Dauerhaft geschlossen", unknown: "Unbekannt" } as const;
export type BusinessStatus = keyof typeof BUSINESS_STATUS;
export const BUSINESS_STATUS_KEYS = Object.keys(BUSINESS_STATUS) as [BusinessStatus, ...BusinessStatus[]];

export const PRICE_LEVELS = { 1: "€", 2: "€€", 3: "€€€", 4: "€€€€" } as const;
export const priceLabel = (n: number | null | undefined) => (n && n in PRICE_LEVELS ? PRICE_LEVELS[n as 1 | 2 | 3 | 4] : "");

/** Prüfung gilt nach so vielen Monaten als veraltet. */
export const STALE_MONTHS = 12;

export const ROUTE_KINDS = {
  hike: { label: "Wanderung", plural: "Wanderungen" },
  roadtrip: { label: "Roadtrip", plural: "Roadtrips" },
  city: { label: "Stadtrundgang", plural: "Stadtrundgänge" },
} as const;
export type RouteKind = keyof typeof ROUTE_KINDS;
export const ROUTE_KIND_KEYS = Object.keys(ROUTE_KINDS) as [RouteKind, ...RouteKind[]];

export const DIFFICULTIES = { leicht: "Leicht", mittel: "Mittel", schwer: "Schwer" } as const;
export type Difficulty = keyof typeof DIFFICULTIES;

export const PUBLISH_STATUS = { draft: "Entwurf", published: "Online" } as const;
export type PublishStatus = keyof typeof PUBLISH_STATUS;

/** Gran Canaria mit Rand; Koordinaten außerhalb gelten als Tippfehler. */
export const ISLAND_BOUNDS = { minLat: 27.6, maxLat: 28.3, minLng: -16.0, maxLng: -15.2 };
export const onIsland = (lat: number, lng: number) =>
  lat >= ISLAND_BOUNDS.minLat && lat <= ISLAND_BOUNDS.maxLat && lng >= ISLAND_BOUNDS.minLng && lng <= ISLAND_BOUNDS.maxLng;

/**
 * Liest Koordinaten so, wie man sie aus Karten-Apps kopiert:
 * "27.740611, -15.587" · "27,7406 -15,587" · 27°44'26.2"N 15°35'13.2"W
 */
export function parseCoords(input: string): { lat: number; lng: number } | null {
  const s = input.trim();
  if (!s) return null;
  const dms = [...s.matchAll(/(\d+(?:[.,]\d+)?)\s*°\s*(?:(\d+(?:[.,]\d+)?)\s*['′]\s*)?(?:(\d+(?:[.,]\d+)?)\s*["″]\s*)?([NSEOW])/gi)];
  if (dms.length === 2) {
    const val = (m: RegExpMatchArray) => {
      const n = (x?: string) => (x ? Number(x.replace(",", ".")) : 0);
      const v = n(m[1]) + n(m[2]) / 60 + n(m[3]) / 3600;
      return /[SW]/i.test(m[4]) ? -v : v;
    };
    const [a, b] = dms;
    const latFirst = /[NS]/i.test(a[4]);
    const lat = val(latFirst ? a : b);
    const lng = val(latFirst ? b : a);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat: round6(lat), lng: round6(lng) } : null;
  }
  // Dezimal: Trenner zwischen den Zahlen ist Komma+Leerzeichen, Semikolon oder Leerzeichen.
  const m = s.match(/^(-?\d{1,2}(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1].replace(",", "."));
  const lng = Number(m[2].replace(",", "."));
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat: round6(lat), lng: round6(lng) };
}
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
export const fmtCoords = (lat: number | null, lng: number | null) => (lat == null || lng == null ? "" : `${lat}, ${lng}`);

export function fmtDuration(min: number | null | undefined) {
  if (!min) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h} Std.` : "", m ? `${m} Min.` : ""].filter(Boolean).join(" ");
}
export const fmtKm = (km: number | null | undefined) =>
  km == null ? "" : `${km.toLocaleString("de-DE", { maximumFractionDigits: km < 10 ? 1 : 0 })} km`;

export const osmUrl = (lat: number, lng: number, zoom = 15) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zoom}/${lat}/${lng}`;
